/**
 * Das Rechnen hinter dem Tagesbrett.
 *
 * Bewusst ohne React und ohne DOM: hier steht, wo ein gezogener Termin
 * landet und wie sich zwei Termine zur selben Zeit die Breite teilen. Beides
 * geht lautlos schief. Ein Termin, der beim Loslassen eine Viertelstunde
 * daneben landet, faellt niemandem auf, bis der Mitarbeiter zu frueh vor der
 * Tuer steht, und ein Termin, der hinter einem anderen verschwindet, faellt
 * erst auf, wenn niemand hinfaehrt.
 *
 * Als reine Funktionen lassen sie sich pruefen, und genau das passiert in
 * test/planlogik.test.ts.
 */

/** "08:30" wird zu 510. */
export function minuten(uhrzeit: string): number {
  const [s, m] = uhrzeit.slice(0, 5).split(":").map(Number);
  return (Number.isFinite(s) ? s : 0) * 60 + (Number.isFinite(m) ? m : 0);
}

/** 510 wird zu "08:30". */
export function alsUhrzeit(minute: number): string {
  const m = Math.max(0, Math.min(24 * 60 - 1, Math.round(minute)));
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

export function begrenzen(wert: number, unten: number, oben: number): number {
  return Math.max(unten, Math.min(oben, wert));
}

export interface Brettmass {
  /** Erste gezeigte Minute des Tages, etwa 420 fuer sieben Uhr. */
  beginn: number;
  /** Letzte gezeigte Minute. */
  ende: number;
  /** Hoehe einer Minute in Pixeln. */
  pxJeMinute: number;
  /** Schrittweite in Minuten. */
  raster: number;
}

/**
 * Der Zeitraum, den das Brett zeigt.
 *
 * Mindestens sieben bis neunzehn Uhr, und darueber hinaus so weit, wie
 * Termine stehen. Ein Brett, das immer um Mitternacht beginnt, verschenkt die
 * halbe Hoehe an eine Nacht, in der niemand arbeitet. Eines, das stur um
 * sieben beginnt, verschluckt den Termin um halb sieben.
 */
export function zeitraum(
  termine: { von: string; bis: string }[],
  mindestens: [number, number] = [7 * 60, 19 * 60]
): [number, number] {
  let a = mindestens[0];
  let b = mindestens[1];
  for (const x of termine) {
    a = Math.min(a, minuten(x.von) - 30);
    b = Math.max(b, minuten(x.bis) + 30);
  }
  return [Math.max(0, Math.floor(a / 60) * 60), Math.min(24 * 60, Math.ceil(b / 60) * 60)];
}

/**
 * Wo ein gezogener Termin landet.
 *
 * `zeigerY` ist der Zeiger auf dem Bildschirm, `kastenOben` der obere Rand der
 * Spalte, `griffY` der Abstand zwischen Zeiger und Blockoberkante beim
 * Anfassen. Ohne den Griff springt der Block beim Loslassen mit der Oberkante
 * unter den Zeiger, und ein Termin, den man in der Mitte anfasst, ruecke
 * dadurch jedes Mal um seine halbe Dauer nach oben.
 */
export function zielzeit(
  zeigerY: number,
  kastenOben: number,
  griffY: number,
  dauer: number,
  mass: Brettmass
): number {
  const roh = mass.beginn + (zeigerY - kastenOben - griffY) / mass.pxJeMinute;
  const gerastert = Math.round(roh / mass.raster) * mass.raster;
  return begrenzen(gerastert, mass.beginn, mass.ende - dauer);
}

export interface Bahn {
  spur: number;
  spuren: number;
}

/**
 * Zwei Termine zur selben Zeit beim selben Mitarbeiter.
 *
 * Sie sind erlaubt, denn manchmal sind sie gewollt, etwa bei einer kurzen
 * Uebergabe. Uebereinander gelegt waere aber einer davon unsichtbar, und ein
 * unsichtbarer Termin ist schlimmer als ein enger. Deshalb teilen sie sich die
 * Breite der Spalte: wer sich ueberschneidet, steht nebeneinander.
 *
 * Gerechnet wird in Gruppen. Alles, was sich beruehrt, gehoert in dieselbe
 * Gruppe und bekommt dieselbe Anzahl Spuren, damit die Kanten untereinander
 * fluchten und nicht jeder Block eine eigene Breite hat.
 */
export function bahnen(liste: { id: string; von: string; bis: string }[]): Map<string, Bahn> {
  const ergebnis = new Map<string, Bahn>();
  const sortiert = [...liste].sort((a, b) => minuten(a.von) - minuten(b.von));

  let gruppe: { id: string; von: string; bis: string }[] = [];
  let gruppenEnde = -1;

  const abschliessen = () => {
    if (gruppe.length === 0) return;
    const spuren: number[] = [];
    const belegt: number[] = [];
    for (const x of gruppe) {
      let spur = belegt.findIndex((endet) => endet <= minuten(x.von));
      if (spur === -1) {
        spur = belegt.length;
        belegt.push(0);
      }
      belegt[spur] = minuten(x.bis);
      spuren.push(spur);
    }
    const anzahl = Math.max(...spuren) + 1;
    gruppe.forEach((x, i) => ergebnis.set(x.id, { spur: spuren[i], spuren: anzahl }));
    gruppe = [];
    gruppenEnde = -1;
  };

  for (const x of sortiert) {
    if (gruppe.length > 0 && minuten(x.von) >= gruppenEnde) abschliessen();
    gruppe.push(x);
    gruppenEnde = Math.max(gruppenEnde, minuten(x.bis));
  }
  abschliessen();
  return ergebnis;
}
